import React, { useState, useEffect } from "react";
import {
  Box,
  Avatar,
  Typography,
  Divider,
  Link,
  Paper,
  Badge,
  Button,
  Switch,
} from "@mui/material";
import { ReactComponent as CheckCircleIcon } from "../../assets/Icons/verify-outlined.svg";
import { ReactComponent as CoffeeCup } from "../../assets/Icons/coffee-cup.svg";
import { ReactComponent as CoffeeCupFilledIcon } from "../../assets/Icons/coffee-cup-filled.svg";
import UpdateStatusMenu from "./UpdateStatusMenu";
import { useBasicInfo } from "../../contexts/BasicInfo";
import {
  onlyAcceptingClientsFrom,
  psypact_provider,
  treatment_program,
} from "../../constants/common";
import { useGeneralProfile } from "../../contexts/Organization";
import { useAuth } from "../../contexts/Auth";
import sortAgeRanges from "../../utils/sortAges";
import paths from "../../constants/paths";
import { trackClickEvent } from "../../mixpanel";
import { session_type } from "../../constants/lists";
import { useCredentials } from "../../contexts/Credentials";

const TherapistInfo = () => {
  const { data, editNetworkingStatus, fetchInfo } = useBasicInfo();
  const { facilityInfo, fetchFacilityInfo } = useGeneralProfile();
  const { account_type, isMentalHealthProfessional, permissions } = useAuth();
  const { fetchPsypact } = useCredentials();
  const [openStatusDrawer, setOpenStatusDrawer] = useState(false);
  const [displayPsypact, setDisplayPsypact] = useState(false);
  const [openToConnecting, setOpenToConnecting] = useState(true);

  const info =
    account_type === treatment_program && !isMentalHealthProfessional()
      ? facilityInfo
      : data;

  const ageServed =
    info?.info?.age_served_currently?.length > 0
      ? sortAgeRanges(info.info.age_served_currently.split(",")).join(",")
      : "";

  useEffect(() => {
    checkPsypactStatus();
  }, []);

  useEffect(() => {
    setOpenToConnecting(info?.info?.open_networking);
  }, [info]);

  const checkPsypactStatus = async () => {
    try {
      const response = await fetchPsypact();
      setDisplayPsypact(
        response?.data?.display_psypact_provider === 1 &&
          account_type !== treatment_program,
      );
    } catch (error) {
      console.error("Error fetching PSYPACT status:", error);
      setDisplayPsypact(false);
    }
  };

  const handleOpenStatusDrawer = (ev) => {
    trackClickEvent("Edit Availability Settings", {
      button: "Edit Availability",
      page: "Homepage",
    });
    ev?.stopPropagation();
    setOpenStatusDrawer(true);
  };

  const updateOpenToNetworking = async (status) => {
    setOpenToConnecting(status);
    await editNetworkingStatus(status, account_type);
    trackClickEvent("Toggled Open to Networking", {
      toggle: status ? "On" : "Off",
      page: "Homepage",
    });
    account_type === treatment_program
      ? await fetchFacilityInfo()
      : await fetchInfo();
  };

  const handleCloseStatusDrawer = () => setOpenStatusDrawer(false);

  return (
    <Paper sx={{ py: 3, mb: 1 }} justifyContent={"center"}>
      <Box
        sx={{
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          px: 2,
        }}
      >
        {/* <Badge
          overlap="circular"
          color={
            info?.info?.current_status === "accepting_new_clients"
              ? "success"
              : "error"
          }
          badgeContent=" "
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={{ mb: 1.5, "& .MuiBadge-badge": { border: "1px solid white" } }}
        > */}
        <Box sx={{ position: "relative", display: "inline-block" }}>
          <Avatar
            sx={{
              width: 72,
              height: 72,
              border: "0.5px solid #0000001A",
            }}
            src={info?.main_image}
          />
          {info?.info?.open_networking ? (
            <Box
              sx={{
                position: "absolute",
                right: -7,
                bottom: 13,
                transform: "translateY(50%)",
                backgroundColor: "#FF8C00",
                border: "2px solid white",
                borderRadius: "50%",
                padding: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                // boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <CoffeeCupFilledIcon width={17} height={17} />
            </Box>
          ) : null}
        </Box>
        {/* </Badge> */}
        <Link
          href={paths.userProfile(
            info?.uuid,
            info?.account_type ? info?.info?.id : "",
            info?.account_type,
          )}
          onClick={() => {
            trackClickEvent("Profile Viewed", {
              page: "Homepage",
              component: "Side Menu",
              button: "View Profile",
            });
          }}
          variant="h5"
          my={1.5}
          color="text.primary"
          textTransform="capitalize"
        >
          {account_type === treatment_program && !isMentalHealthProfessional()
            ? facilityInfo?.info?.facility_name
            : `${data.first_name || ""} ${data.last_name || ""}`}
        </Link>
      </Box>
      {
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "auto",
            mx: 1,
            px: 1,
            justifyContent: "space-around",
            backgroundColor: "#FF8C001A",
            borderRadius: "20px",
            padding: "2px 2px",
            // flexWrap: "wrap",
            minHeight: "40px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              // gap: 1,
              // flex: "1 1 auto",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                px: 1,
                py: 0.625,
                color: "#FF8C00",
                flexShrink: 0,
                alignItems: "center",
              }}
            >
              <CoffeeCup stroke="#FF8C00" />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "#000",
                fontSize: "14px",
                fontWeight: 600,
                wordBreak: "break-word",
                alignItems: "center",
                lineHeight: 1.2,
              }}
            >
              Open to Networking
            </Typography>
          </Box>
          <Box
            onClick={
              permissions?.network_write
                ? () => updateOpenToNetworking(!openToConnecting)
                : undefined
            }
            sx={{
              width: 36,
              height: 18,
              backgroundColor: openToConnecting ? "#FF8C00" : "#E0E0E0",
              borderRadius: "12px",
              position: "relative",
              cursor: permissions?.network_write ? "pointer" : "not-allowed",
              transition: "background-color 0.3s ease",
              display: "flex",
              alignItems: "center",
              padding: "2px",
              mt: "3px",
              flexShrink: 0,
              opacity: permissions?.network_write ? 1 : 0.5,
            }}
          >
            <Box
              sx={{
                width: 17,
                height: 17,
                backgroundColor: "white",
                borderRadius: "50%",
                position: "absolute",
                left: openToConnecting ? "19px" : "2px",
                transition: "left 0.1s ease",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              }}
            />
          </Box>
        </Box>
      }
      <Divider sx={{ my: 1.5 }} />
      <Box px={1.5}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "text.secondary" }}
            fontWeight={600}
          >
            My Availability
          </Typography>
          <Button
            color="primary"
            size="small"
            onClick={handleOpenStatusDrawer}
            sx={{ ml: "auto", p: 0, minWidth: 0 }}
            disabled={!permissions?.availability_update}
          >
            Edit
          </Button>
        </Box>
        <UpdateStatusMenu
          openStatusDrawer={openStatusDrawer}
          handleOpenStatusDrawer={handleOpenStatusDrawer}
          handleCloseStatusDrawer={handleCloseStatusDrawer}
          disabled={!permissions?.availability_update}
        />
        <Box sx={{ my: 2 }}>
          {info?.info?.current_status &&
          info?.info?.current_status === "accepting_new_clients" ? (
            <>
              {info?.currently_served?.length > 0 && (
                <Paper sx={{ my: 2 }}>
                  <Box sx={{ py: 0.5, px: 2 }}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      Types
                    </Typography>
                  </Box>
                  <Divider />
                  <Divider />
                  <Box
                    sx={{
                      py: 1,
                      px: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        alignItems: "center",
                      }}
                    >
                      {info.currently_served?.map((item, index, array) => (
                        <Typography
                          key={item?.id || index}
                          component="span"
                          variant="body2"
                          sx={{
                            whiteSpace: "nowrap", // Keep each item together
                          }}
                        >
                          {item?.name}
                          {index < array.length - 1 ? "," : ""}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              )}
              {info?.info?.age_served_currently && (
                <Paper sx={{ my: 2 }}>
                  <Box sx={{ py: 0.5, px: 2 }}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      Ages
                    </Typography>
                  </Box>
                  <Divider />
                  <Divider />
                  <Box
                    sx={{
                      py: 1,
                      px: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      overflow: "wrap",
                      wordBreak: "break-all",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {ageServed.split(",").filter(Boolean).join(", ")}
                    </Typography>
                  </Box>
                </Paper>
              )}
              {info?.info?.session_types_currently && (
                <Paper sx={{ my: 2 }}>
                  <Box sx={{ py: 0.5, px: 2 }}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      Session Types
                    </Typography>
                  </Box>
                  <Divider />
                  <Divider />
                  <Box
                    sx={{
                      py: 1,
                      px: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      overflow: "wrap",
                      wordBreak: "break-all",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {session_type
                        .filter((element) => {
                          if (element.value === psypact_provider.value) {
                            return (
                              displayPsypact &&
                              info?.info?.psypact_provider === 1
                            );
                          }
                          return info?.info?.session_types_currently?.[
                            element.value
                          ];
                        })
                        ?.map((item) => item.key)
                        ?.join(", ")}
                    </Typography>
                  </Box>
                </Paper>
              )}
              {info?.info?.accepting_referrals_from && (
                <Paper sx={{ my: 2 }}>
                  <Box sx={{ py: 0.5, px: 2 }}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      Accepting Referrals From
                    </Typography>
                  </Box>
                  <Divider />
                  <Divider />
                  <Box
                    sx={{
                      py: 1,
                      px: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      overflow: "wrap",
                      wordBreak: "break-all",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {
                        onlyAcceptingClientsFrom[
                          info?.info?.accepting_referrals_from
                        ]
                      }
                    </Typography>
                  </Box>
                </Paper>
              )}
            </>
          ) : null}
        </Box>
      </Box>
    </Paper>
  );
};

export default TherapistInfo;
